/**
 * Header actions for a reseller (TMMPDM-0052, TMMPDM-0053). Suspend / Reactivate stays a
 * button. The header ⋮ holds Edit details… (name only), GHL connection…, then Deactivate…, and Delete… with the same dialogs, rules and disabled states.
 * The cluster is the same on every section.
 */
import { useState, type ReactNode } from 'react';
import { Alert, Box, Button, Stack } from '@mui/material';
import { useCopy } from '@/hooks/useCopy';
import {
  ConfirmDialog,
  EditDetailsDialog,
  RowActionsMenu,
  fieldEditable,
  hasEditableFields,
  type RowActionItem,
} from '@/components/page';
import { adminApi } from '@/api/tmm/admin';
import { ApiError, errorCopy, isOutcomeUnknown } from '@/api/errors';
import type { ResellerDto, ResellerStandingState } from '@/api/tmm/types';
import { DeactivateResellerDialog } from './DeactivateResellerDialog';

type Confirm = 'suspend' | 'reactivate' | 'delete' | 'house' | null;

export interface ResellerObjectActions {
  /** Suspend / Reactivate. The ⋮ menu is `menu`, beside the name. */
  actions: ReactNode;
  menu: ReactNode;
  /** Rename / status / delete failures, shown above the sections. */
  banners: ReactNode;
  dialogs: ReactNode;
  openDeactivate: () => void;
  /** True while a header write is in flight — alert buttons wait. */
  busy: boolean;
}

export function useResellerObjectActions({
  reseller,
  standingState,
  onUpdated,
  onDeleted,
  onReload,
}: {
  /** Null while the page loads: the hook is then inert (the page's header is drawn from the first render). */
  reseller: ResellerDto | null;
  standingState: ResellerStandingState | null;
  onUpdated: (reseller: ResellerDto) => void;
  onDeleted: () => void;
  onReload: () => void;
}): ResellerObjectActions {
  const { t } = useCopy();
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [detailsToken, setDetailsToken] = useState(0);
  const [confirm, setConfirm] = useState<Confirm>(null);
  const [busy, setBusy] = useState(false);
  // A refused action is said inside its confirm, which stays open (TMM-B4).
  const [confirmError, setConfirmError] = useState<string | null>(null);
  const [outcomeUnknown, setOutcomeUnknown] = useState(false);
  const [deactivateOpen, setDeactivateOpen] = useState(false);

  if (reseller === null) {
    return {
      actions: null,
      menu: null,
      banners: null,
      dialogs: null,
      openDeactivate: () => undefined,
      busy,
    };
  }

  const canDeactivate = standingState !== 'DEACTIVATED';
  const canToggleStatus = standingState !== 'DEACTIVATED';
  const suspended = reseller.status === 'SUSPENDED';

  const reloadAfterUnknownOutcome = () => {
    setOutcomeUnknown(true);
    onReload();
  };

  const handleConfirm = async () => {
    const action = confirm;
    setConfirmError(null);
    setOutcomeUnknown(false);
    setBusy(true);
    try {
      if (action === 'delete') {
        await adminApi.deleteReseller(reseller.id);
        onDeleted();
      } else if (action === 'house') {
        await adminApi.updateReseller(reseller.id, { platformOwned: !reseller.platformOwned });
        onReload();
      } else if (action) {
        const updated = await adminApi.updateReseller(reseller.id, {
          status: action === 'suspend' ? 'SUSPENDED' : 'ACTIVE',
        });
        onUpdated(updated);
      }
      setConfirm(null);
    } catch (err) {
      if (isOutcomeUnknown(err)) {
        setConfirm(null);
        reloadAfterUnknownOutcome();
        return;
      }
      if (err instanceof ApiError && String(err.code) === 'RESELLER_STANDING_CONFLICT') {
        setConfirmError(
          action === 'house'
            ? t(
                'partners.house.standingConflict',
                "A partner under deactivation can't be marked as house. Reactivate it first.",
              )
            : t(
                'resellers.overview.statusStandingConflict',
                "This reseller is deactivated, so its status can't be changed here. Use Reactivate… to bring it back. The screen has been reloaded.",
              ),
        );
        onReload();
        return;
      }
      setConfirmError(
        errorCopy(
          err,
          t,
          action === 'delete'
            ? t('resellers.overview.deleteError', 'Could not delete reseller.')
            : action === 'house'
              ? t('partners.house.error', 'Could not change the house flag.')
              : t('resellers.overview.statusError', 'Could not change reseller status.'),
        ),
      );
    } finally {
      setBusy(false);
    }
  };

  const menuItems: RowActionItem[] = [];
  if (hasEditableFields(reseller.editableFields)) {
    menuItems.push({
      key: 'details',
      label: t('components.editDetails.menu', 'Edit details…'),
      disabled: busy,
      onSelect: () => setDetailsOpen(true),
    });
  }
  if (reseller.platformOwned || reseller.platformOwnedSettable)
    menuItems.push({
      key: 'house',
      label: reseller.platformOwned
        ? t('partners.house.unmark', 'Unmark as house partner…')
        : t('partners.house.mark', 'Mark as house partner…'),
      disabled: busy,
      onSelect: () => setConfirm('house'),
    });
  if (canDeactivate) {
    menuItems.push({
      key: 'deactivate',
      label: t('resellers.overview.deactivate', 'Deactivate…'),
      destructive: true,
      disabled: busy,
      onSelect: () => setDeactivateOpen(true),
    });
  }
  menuItems.push({
    key: 'delete',
    label: t('resellers.overview.delete', 'Delete…'),
    destructive: true,
    disabled: reseller.tenantCount !== 0 || busy,
    onSelect: () => setConfirm('delete'),
  });

  const menu = <RowActionsMenu name={reseller.name} items={menuItems} disabled={busy} />;

  const actions = (
    <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap', alignItems: 'center' }}>
      {canToggleStatus ? (
        <Button
          variant="outlined"
          color={suspended ? 'primary' : 'warning'}
          onClick={() => setConfirm(suspended ? 'reactivate' : 'suspend')}
          disabled={busy}
        >
          {suspended
            ? t('resellers.overview.reactivate', 'Reactivate')
            : t('resellers.overview.suspend', 'Suspend')}
        </Button>
      ) : null}
    </Stack>
  );

  const banners = (
    <Box>
      {outcomeUnknown ? (
        <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setOutcomeUnknown(false)}>
          {t(
            'common.outcomeUnknown',
            "We couldn't confirm the change. The screen has been reloaded — check before trying again.",
          )}
        </Alert>
      ) : null}
    </Box>
  );

  const dialogs = (
    <>
      <EditDetailsDialog
        open={detailsOpen}
        entityLabel={t('components.editDetails.entity.reseller', 'reseller')}
        fields={
          fieldEditable(reseller.editableFields, 'name')
            ? [
                {
                  key: 'name',
                  label: t('resellers.create.nameLabel', 'Name'),
                  value: reseller.name,
                  required: true,
                  helperText: t(
                    'resellers.overview.renameHelper',
                    'The reseller brand follows the new name unless it was customised.',
                  ),
                },
              ]
            : []
        }
        version={null}
        validate={(values) => {
          if ((values.name ?? '').trim().length > 200) {
            return {
              name: t('resellers.create.nameTooLong', 'Name must be 200 characters or fewer.'),
            };
          }
          return {};
        }}
        onClose={() => setDetailsOpen(false)}
        onReload={() => {
          onReload();
          setDetailsToken((n) => n + 1);
        }}
        reloadToken={detailsToken}
        onOutcomeUnknown={reloadAfterUnknownOutcome}
        onSubmit={async (changed) => {
          if (changed.name === undefined) return;
          const updated = await adminApi.updateReseller(reseller.id, { name: changed.name.trim() });
          onUpdated(updated);
        }}
      />
      <ConfirmDialog
        open={confirm !== null}
        title={
          confirm === 'house'
            ? reseller.platformOwned
              ? t('partners.house.unmarkTitle', 'Unmark {{partner}} as the house partner?', {
                  partner: reseller.name,
                })
              : t('partners.house.markTitle', 'Mark {{partner}} as the house partner?', {
                  partner: reseller.name,
                })
            : confirm === 'delete'
              ? t('resellers.overview.deleteTitle', 'Delete {{name}}?', { name: reseller.name })
              : confirm === 'reactivate'
                ? t('resellers.overview.reactivateTitle', 'Reactivate {{name}}?', {
                    name: reseller.name,
                  })
                : t('resellers.overview.suspendTitle', 'Suspend {{name}}?', { name: reseller.name })
        }
        consequence={
          confirm === 'house'
            ? t(
                'partners.house.consequence',
                "The house partner is ours: it's never evaluated or deactivated.",
              )
            : confirm === 'delete'
              ? t(
                  'resellers.overview.deleteBody',
                  'This deletes the reseller and its brand. It cannot be undone.',
                )
              : confirm === 'reactivate'
                ? t('resellers.overview.reactivateBody', 'Its users can sign in again.')
                : t(
                    'resellers.overview.suspendBody',
                    "Its users can't sign in. Its tenants keep working.",
                  )
        }
        confirmLabel={
          confirm === 'house'
            ? t('common.confirm', 'Confirm')
            : confirm === 'delete'
              ? t('resellers.overview.deleteConfirm', 'Delete')
              : confirm === 'reactivate'
                ? t('resellers.overview.reactivate', 'Reactivate')
                : t('resellers.overview.suspend', 'Suspend')
        }
        destructive={confirm === 'delete' || confirm === 'suspend'}
        busy={busy}
        error={confirmError}
        onConfirm={() => void handleConfirm()}
        onCancel={() => {
          setConfirm(null);
          setConfirmError(null);
        }}
      />
      <DeactivateResellerDialog
        open={deactivateOpen}
        from={reseller}
        onClose={() => setDeactivateOpen(false)}
        onDone={onReload}
        onOutcomeUnknown={reloadAfterUnknownOutcome}
      />
    </>
  );

  return {
    actions,
    menu,
    banners,
    dialogs,
    openDeactivate: () => setDeactivateOpen(true),
    busy,
  };
}
